'use client';

import { c as _c } from "react/compiler-runtime";
import type { Post, PostResponse } from '@shared/all/domains/posts';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import usePostsShared from '@shared/utils/hooks/usePosts';
import { type InfiniteData, keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, X } from 'lucide-react';
import { useState } from 'react';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import Input from '../UI/Input';
type Props = {
  post: Post;
  closeHandler: () => void;
};
const TagUsersModal = t0 => {
  const $ = _c(74);
  const {
    post,
    closeHandler
  } = t0;
  const [searchQuery, setSearchQuery] = useState("");
  let t1;
  if ($[0] !== post.taggedUsers) {
    t1 = post.taggedUsers?.map(_temp) || [];
    $[0] = post.taggedUsers;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [selectedUserIds, setSelectedUserIds] = useState(t1);
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();
  const {
    queryKey
  } = usePostsShared(client);
  let t2;
  let t3;
  if ($[2] !== post.space) {
    t2 = ["space", post.space];
    t3 = () => unwrapFromResult(client.spaces.get.query({
      slug: post.space || ""
    }));
    $[2] = post.space;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  const t4 = !!post.space;
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      staleTime: 300000
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data: spaceData
  } = useQuery(t5);
  let t6;
  if ($[9] !== post.space || $[10] !== searchQuery) {
    t6 = ["search", "users", searchQuery, post.space];
    $[9] = post.space;
    $[10] = searchQuery;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== searchQuery || $[13] !== selectedUserIds || $[14] !== spaceData) {
    t7 = () => unwrapFromResult(client.search.users.query({
      query: searchQuery || undefined,
      spaceIds: spaceData?.id ? [spaceData.id] : undefined,
      ignoreUserIds: selectedUserIds
    }));
    $[12] = searchQuery;
    $[13] = selectedUserIds;
    $[14] = spaceData;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = !post.space || !!spaceData;
  let t9;
  if ($[16] !== t6 || $[17] !== t7 || $[18] !== t8) {
    t9 = {
      queryKey: t6,
      queryFn: t7,
      enabled: t8,
      placeholderData: keepPreviousData,
      staleTime: 30000
    };
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const {
    data,
    isLoading
  } = useQuery(t9);
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = userId => {
      setSelectedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
    };
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const toggleUserSelection = t10;
  let t11;
  let users;
  if ($[21] !== data?.users || $[22] !== post.taggedUsers || $[23] !== selectedUserIds) {
    users = data?.users || [];
    let t12;
    if ($[26] !== selectedUserIds) {
      t12 = u_1 => selectedUserIds.includes(u_1.id);
      $[26] = selectedUserIds;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    t11 = [...(post.taggedUsers || []).filter(u_0 => selectedUserIds.includes(u_0.id)), ...users.filter(t12)].filter(_temp2);
    $[21] = data?.users;
    $[22] = post.taggedUsers;
    $[23] = selectedUserIds;
    $[24] = t11;
    $[25] = users;
  } else {
    t11 = $[24];
    users = $[25];
  }
  const allSelectedUsers = t11;
  let t12;
  if ($[28] !== allSelectedUsers || $[29] !== closeHandler || $[30] !== post.id || $[31] !== post.taggedUsers || $[32] !== queryClient || $[33] !== queryKey || $[34] !== selectedUserIds) {
    t12 = async () => {
      setIsSaving(true);
      const currentTaggedUserIds = post.taggedUsers?.map(_temp3) || [];
      const usersToAdd = selectedUserIds.filter(id_0 => !currentTaggedUserIds.includes(id_0));
      const usersToRemove = currentTaggedUserIds.filter(id_1 => !selectedUserIds.includes(id_1));
      await Promise.all([usersToAdd.length > 0 ? client.posts.tagUsers.mutate({
        postId: post.id,
        userIds: usersToAdd
      }) : Promise.resolve(), usersToRemove.length > 0 ? client.posts.untagUsers.mutate({
        postId: post.id,
        userIds: usersToRemove
      }) : Promise.resolve()]);
      queryClient.setQueriesData({
        queryKey
      }, oldData => {
        if (!oldData) {
          return oldData;
        }
        return {
          ...oldData,
          pages: oldData.pages.map(page => ({
            ...page,
            posts: page.posts.map(p => p.id === post.id ? {
              ...p,
              taggedUsers: allSelectedUsers.map(_temp4)
            } : p)
          }))
        };
      });
      closeHandler();
      setIsSaving(false);
    };
    $[28] = allSelectedUsers;
    $[29] = closeHandler;
    $[30] = post.id;
    $[31] = post.taggedUsers;
    $[32] = queryClient;
    $[33] = queryKey;
    $[34] = selectedUserIds;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const handleSave = t12;
  let t13;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <h2>Tag Members</h2>;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== closeHandler) {
    t14 = <ModalHeader closeHandler={closeHandler}>{t13}</ModalHeader>;
    $[37] = closeHandler;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== searchQuery) {
    t15 = <Input type="text" label="Search members" value={searchQuery} onChange={setSearchQuery} autofocus={true} />;
    $[39] = searchQuery;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== allSelectedUsers) {
    t16 = allSelectedUsers.length > 0 && <div className="space-y-2"><p className="text-sm font-emphasis text-textSecondary">Selected ({allSelectedUsers.length})</p><div className="flex flex-wrap gap-2">{allSelectedUsers.map(user_0 => <div key={`selected-${user_0.id}`} className="flex items-center gap-2 rounded-md bg-background-secondary px-3 py-1.5 text-sm"><Avatar avatarKey={user_0.avatarKey ?? null} robohash={user_0.robohash ?? ""} isBot={user_0.tag === "BOT"} size={20} userId={user_0.id} /><span>{user_0.name || `@${user_0.username}`}</span><button onClick={() => toggleUserSelection(user_0.id)} className="text-textSecondary hover:text-textPrimary"><X size={14} /></button></div>)}</div></div>;
    $[41] = allSelectedUsers;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== isLoading) {
    t17 = isLoading && <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin text-textSecondary" size={24} /></div>;
    $[43] = isLoading;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== isLoading || $[46] !== users.length) {
    t18 = !isLoading && users.length === 0 && <p className="py-8 text-center text-sm text-textSecondary/60">No users found</p>;
    $[45] = isLoading;
    $[46] = users.length;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== selectedUserIds || $[49] !== users) {
    let t20;
    if ($[51] !== selectedUserIds) {
      t20 = user_1 => {
        const isSelected = selectedUserIds.includes(user_1.id);
        return <div key={user_1.id} className={`flex cursor-pointer items-center justify-between rounded-md p-3 hover:bg-background-light ${isSelected ? "bg-background-secondary" : ""}`} onClick={() => toggleUserSelection(user_1.id)}><div className="flex items-center gap-x-2"><Avatar avatarKey={user_1.avatarKey ?? null} robohash={user_1.robohash ?? ""} isBot={user_1.tag === "BOT"} size={35} userId={user_1.id} />{user_1.name ? <p>{user_1.name} (@{user_1.username})</p> : <p>@{user_1.username}</p>}</div>{isSelected && <Check size={18} className="text-primary" />}</div>;
      };
      $[51] = selectedUserIds;
      $[52] = t20;
    } else {
      t20 = $[52];
    }
    t19 = users.map(t20);
    $[48] = selectedUserIds;
    $[49] = users;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[53] !== t17 || $[54] !== t18 || $[55] !== t19) {
    t20 = <div className="h-[300px] space-y-1 overflow-y-auto rounded-md border border-ds-border-standard p-2">{t17}{t18}{t19}</div>;
    $[53] = t17;
    $[54] = t18;
    $[55] = t19;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== t15 || $[58] !== t16 || $[59] !== t20) {
    t21 = <ModalContent><div className="space-y-4">{t15}{t16}{t20}</div></ModalContent>;
    $[57] = t15;
    $[58] = t16;
    $[59] = t20;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== closeHandler) {
    t22 = <Button text="Cancel" variant="SECONDARY" onClick={closeHandler} />;
    $[61] = closeHandler;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== handleSave || $[64] !== isSaving) {
    t23 = <Button text="Save" onClick={handleSave} disabled={isSaving} loading={isSaving} />;
    $[63] = handleSave;
    $[64] = isSaving;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== t22 || $[67] !== t23) {
    t24 = <ModalFooter>{t22}{t23}</ModalFooter>;
    $[66] = t22;
    $[67] = t23;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== closeHandler || $[70] !== t14 || $[71] !== t21 || $[72] !== t24) {
    t25 = <Modal closeHandler={closeHandler}>{t14}{t21}{t24}</Modal>;
    $[69] = closeHandler;
    $[70] = t14;
    $[71] = t21;
    $[72] = t24;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  return t25;
};
export default TagUsersModal;
function _temp(u) {
  return u.id;
}
function _temp2(user, index, self) {
  return index === self.findIndex(u_2 => u_2.id === user.id);
}
function _temp3(u_3) {
  return u_3.id;
}
function _temp4(u_4) {
  return {
    id: u_4.id,
    username: u_4.username,
    name: u_4.name,
    avatarKey: u_4.avatarKey,
    robohash: u_4.robohash,
    tag: u_4.tag,
    anonymous: false
  };
}